import React, { useEffect } from "react"
import { Check } from "lucide-mui"
import i18n from "@/i18n"
import { useVenue } from "@/contexts/venue"
import { useNavigate } from "react-router-dom"
import { usePanelLink } from "@/shared/hooks"

const STYLES = {
  container: "bg-white rounded-lg p-6",
  table: "w-full border-collapse",
  th: "text-center text-sm font-semibold py-4 px-2",
  thFirst: "text-left py-4 px-3 w-1/2",
  td: "py-3 px-3 border border-gray-300 border-solid",
  tdCenter: "py-3 px-2 border border-gray-300 border-solid text-center",
  permissionName: "font-medium text-black mb-1",
  permissionDescription: "text-sm text-gray-600",
} as const

const RolesPermissions: React.FC = () => {
  const { venue } = useVenue()
  const navigate = useNavigate()
  const linkTo = usePanelLink()

  useEffect(() => {
    if (!venue.permissions?.manageUsers) {
      navigate(linkTo("settings"))
    }
  }, [venue.permissions?.manageUsers, navigate, linkTo])

  const permissions = [
    {
      key: "view_all_reservations",
      staff: true,
      manager: true,
      admin: true,
    },
    {
      key: "modify_reservations",
      staff: true,
      manager: true,
      admin: true,
    },
    {
      key: "manage_venues",
      staff: false,
      manager: true,
      admin: true,
    },
    {
      key: "view_reports",
      staff: false,
      manager: true,
      admin: true,
    },
    {
      key: "manage_users",
      staff: false,
      manager: false,
      admin: true,
    },
    {
      key: "access_billing",
      staff: false,
      manager: false,
      admin: true,
    },
  ]

  return (
    <div className="flex flex-col gap-6 max-w-4xl" data-testid="settings-roles-page">
      <h1 className="text-3xl font-bold mb-0" data-testid="settings-roles-title">
        {i18n.t("app.settings.users.roles.title")}
      </h1>

      <div className={STYLES.container}>
        <table className={STYLES.table} data-testid="settings-roles-table">
          <thead>
            <tr>
              <th className={STYLES.thFirst} />
              <th className={STYLES.th}>{i18n.t("app.settings.users.roles.table.admin")}</th>
              <th className={STYLES.th}>{i18n.t("app.settings.users.roles.table.manager")}</th>
              <th className={STYLES.th}>{i18n.t("app.settings.users.roles.table.staff")}</th>
            </tr>
          </thead>
          <tbody>
            {permissions.map((permission) => (
              <tr key={permission.key} data-testid={`settings-roles-row-${permission.key}`}>
                <td className={STYLES.td}>
                  <div className={STYLES.permissionName}>
                    {i18n.t(`app.settings.users.roles.permissions.${permission.key}.name`)}
                  </div>
                  <div className={STYLES.permissionDescription}>
                    {i18n.t(`app.settings.users.roles.permissions.${permission.key}.description`)}
                  </div>
                </td>
                <td className={STYLES.tdCenter}>
                  {permission.admin && <Check className="w-5 h-5 text-green-600 inline-block" />}
                </td>
                <td className={STYLES.tdCenter}>
                  {permission.manager && <Check className="w-5 h-5 text-green-600 inline-block" />}
                </td>
                <td className={STYLES.tdCenter}>
                  {permission.staff && <Check className="w-5 h-5 text-green-600 inline-block" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default RolesPermissions
